import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useGetAudioPreview } from './useGetAudioPreview';
import { DbModelToDisplayName } from 'apps/t2c-fe/utils';

interface AudioPreview {
  data: number[];
  type: 'Buffer';
}

interface UseAudioPreviewProps {
  text: string;
  audioId: string;
  modelName: string;
}

export const useAudioPreview = ({ text, audioId, modelName }: UseAudioPreviewProps) => {
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentText, setCurrentText] = useState(text);
  const [currentAudioId, setCurrentAudioId] = useState(audioId);
  const [currentModelName, setCurrentModelName] = useState(modelName);
  const [isFetchingAudio, setIsFetchingAudio] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const { mutate: mutateGetAudioPreview } = useGetAudioPreview();

  const fetchAudioPreview = useCallback(
    (newText: string, newAudioId: string, newModelName: string) => {
      setIsFetchingAudio(true);
      mutateGetAudioPreview(
        { text: newText, audioId: newAudioId, modelName: DbModelToDisplayName[newModelName] },
        {
          onSuccess: (data: AudioPreview) => {
            if (data?.data) {
              const byteArray = new Uint8Array(data.data);
              const audioBlob = new Blob([byteArray], { type: 'audio/mpeg' });
              const url = URL.createObjectURL(audioBlob);
              setPreviewURL(url);
              setPlaying(true);
              setCurrentText(newText);
              setCurrentAudioId(newAudioId);
              setCurrentModelName(newModelName);
            }
            setIsFetchingAudio(false);
          },
          onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || error?.message || 'Error fetching audio preview. Please try again.', {
              variant: 'error',
            });
            setIsFetchingAudio(false);
          },
        }
      );
    },
    [mutateGetAudioPreview, enqueueSnackbar]
  );


  const togglePlayPause = useCallback(() => {
    if (!text.trim()) {
      enqueueSnackbar('Please enter text for preview', { variant: 'error' });
      return;
    }

    if (currentText !== text || currentAudioId !== audioId || !previewURL || currentModelName !== modelName) {
      fetchAudioPreview(text, audioId, modelName);
    } else {
      setPlaying((prev) => !prev);
    }
  }, [text, audioId, modelName, currentText, currentAudioId, currentModelName, fetchAudioPreview, enqueueSnackbar,previewURL]);

  const handlePausePlayer = () => {
    setPlaying(false);
  };

  useEffect(() => {
    return () => {
      if (previewURL) {
        URL.revokeObjectURL(previewURL);
      }
    };
  }, [previewURL]);

  return {
    previewURL,
    playing,
    isFetchingAudio,
    togglePlayPause,
    handlePausePlayer,
  };
};
